/**
 * Sync engine: drains the IndexedDB outbox through POST /api/sync/points,
 * adopts server rows on version conflicts, then pulls incremental changes.
 * UI reads state through useSyncState(); queuePoint() is the write path.
 */
import { syncPullResponseSchema, syncPushResponseSchema, type PointDto } from '@datum/shared'
import type { QueryClient } from '@tanstack/react-query'
import { api } from './api'
import {
  buildSyncChange,
  enqueueChange,
  getKv,
  putCachedPoints,
  removeOutbox,
  listOutbox,
  setKv,
} from './offline'

export type SyncPhase = 'idle' | 'syncing' | 'offline' | 'error'

export interface SyncState {
  phase: SyncPhase
  /** Point ids with queued, un-pushed edits (amber). */
  pending: string[]
  /** Point ids where the server rejected our version (red, until acknowledged). */
  conflicts: string[]
  lastSyncAt: number | null
}

let state: SyncState = {
  phase: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'idle',
  pending: [],
  conflicts: [],
  lastSyncAt: null,
}

const listeners = new Set<() => void>()

function emit(patch: Partial<SyncState>): void {
  state = { ...state, ...patch }
  for (const listener of listeners) listener()
}

export const syncStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  get(): SyncState {
    return state
  },
}

function withPending(mutate: (ids: Set<string>) => void): void {
  const ids = new Set(state.pending)
  mutate(ids)
  emit({ pending: [...ids] })
}

function withConflicts(mutate: (ids: Set<string>) => void): void {
  const ids = new Set(state.conflicts)
  mutate(ids)
  emit({ conflicts: [...ids] })
}

export function resolveConflict(pointId: string): void {
  withConflicts((ids) => ids.delete(pointId))
}

/** 409 that arrives outside the drain path (e.g. AI processed a stale row):
 *  adopt the authoritative server row and surface the red ack banner, same as
 *  pushOutbox does for queued conflicts. */
export function adoptConflictServer(server: PointDto): void {
  void putCachedPoints([server])
  withConflicts((ids) => ids.add(server.id))
}

let draining = false
let drainTimer: ReturnType<typeof setTimeout> | null = null

export function scheduleDrain(delayMs = 700): void {
  if (drainTimer !== null) clearTimeout(drainTimer)
  drainTimer = setTimeout(() => {
    drainTimer = null
    void drain()
  }, delayMs)
}

/** The single path every edit takes: mirror locally, queue, drain when online. */
export async function queuePoint(point: PointDto, baseVersion: number): Promise<void> {
  await putCachedPoints([point])
  await enqueueChange({ pointId: point.id, change: buildSyncChange(point, baseVersion), queuedAt: Date.now() })
  withPending((ids) => ids.add(point.id))
  scheduleDrain()
}

async function pushOutbox(): Promise<void> {
  const entries = await listOutbox()
  if (entries.length === 0) return
  const response = await api.api.sync.points.$post({ json: { changes: entries.map((e) => e.change) } })
  if (!response.ok) {
    throw new Error(`sync push failed (${response.status})`)
  }
  const parsed = syncPushResponseSchema.safeParse(await response.json())
  if (!parsed.success) {
    throw new Error('sync push returned an unexpected payload')
  }

  for (const result of parsed.data.results) {
    await removeOutbox(result.id)
    withPending((ids) => ids.delete(result.id))
    if (result.status === 'conflict') {
      // Optimistic concurrency: the server row is authoritative until the
      // user chooses to re-edit; cache it and flag the timeline red.
      await putCachedPoints([result.server])
      withConflicts((ids) => ids.add(result.id))
    }
  }
}

async function pullChanges(): Promise<void> {
  let since = (await getKv<number>('lastPulledAt')) ?? 0
  let serverTime = since

  // Bounded paging: notes sharing one updated_at can reappear across pages;
  // clients upsert by id+version, so overlap is harmless, just capped here.
  for (let page = 0; page < 20; page++) {
    const response = await api.api.sync.changes.$get({ query: { since: String(since), limit: '100' } })
    if (!response.ok) break
    const parsed = syncPullResponseSchema.safeParse(await response.json())
    if (!parsed.success) break
    await putCachedPoints(parsed.data.points)
    serverTime = parsed.data.server_time
    if (parsed.data.next_since === null) break
    since = parsed.data.next_since
  }

  await setKv('lastPulledAt', serverTime)
}

export async function drain(): Promise<void> {
  if (draining) return
  draining = true
  try {
    if (!navigator.onLine) {
      emit({ phase: 'offline' })
      return
    }
    emit({ phase: 'syncing' })
    await pushOutbox()
    await pullChanges()
    emit({ phase: 'idle', lastSyncAt: Date.now() })
  } catch (error) {
    console.warn('[datum] sync drain failed', error)
    emit({ phase: navigator.onLine ? 'error' : 'offline' })
  } finally {
    draining = false
    queryClient?.invalidateQueries({ queryKey: POINTS_QUERY_KEY })
  }
}

export const POINTS_QUERY_KEY = ['points'] as const

let queryClient: QueryClient | null = null

/** Wire the engine into the app: invalidates queries after drains, retries on reconnect. */
export function initSyncEngine(client: QueryClient): void {
  queryClient = client
  window.addEventListener('online', () => {
    void drain()
  })
  window.addEventListener('focus', () => {
    void drain()
  })
  // Anything left from a previous session goes out immediately.
  void drain()
}
